import type {
  DomainEventPublisher,
  GermanLegalInfo,
  InvoiceData,
  InvoiceFinancials,
  InvoiceNumberValidator,
  PDFRenderer,
  ShippingCostPolicy,
  ShippingRateProvider,
} from "@invoicing-sdk/domain";
import {
  Invoice,
  InvoiceDraftNotFoundError,
  InvoiceIntegrityCheckError,
  InvoiceStateViolationError,
  InvoiceStatus,
  OrderNotFoundError,
  ShippingCost,
} from "@invoicing-sdk/domain";
import type { InvoiceRepository, OrderRepository } from "../ports/index.js";
import type { IssueInvoiceCommand, IssueInvoiceResult } from "./commands.js";

/**
 * Dependencies required for IssueInvoice use case
 */
export interface IssueInvoiceDependencies {
  readonly orderRepository: OrderRepository;
  readonly invoiceRepository: InvoiceRepository;
  readonly invoiceNumberValidator: InvoiceNumberValidator;
  readonly pdfRenderer: PDFRenderer;
  readonly legalInfo: GermanLegalInfo;
  readonly shippingRateProvider: ShippingRateProvider;
  readonly shippingCostPolicy: ShippingCostPolicy;
  /**
   * Event publisher to publish {@link InvoiceIssued} events to.
   *
   * Can be omitted to not use event publishing.
   */
  readonly eventPublisher?: DomainEventPublisher;
}

/**
 * Use case for issuing and finalizing an invoice
 * This issues the invoice with validation, PDF generation, and persistence
 *
 * Requirements:
 * - 1.1: Ensure the draft exists
 * - 1.2: Snapshot customer information and order items
 * - 1.3: Validate invoice number format
 * - 1.4: Calculate totals correctly
 * - 1.5: Generate PDF/A-3 compliant PDF
 * - 1.6: Make invoice immutable after issuance
 */
export function makeIssueInvoice(deps: IssueInvoiceDependencies) {
  return async (command: IssueInvoiceCommand): Promise<IssueInvoiceResult> => {
    const {
      orderRepository,
      invoiceRepository,
      invoiceNumberValidator,
      shippingRateProvider,
      shippingCostPolicy,
      pdfRenderer,
      legalInfo,
    } = deps;
    const { invoiceNumber, orderId, issuedBy } = command;

    // Check if order exists
    const order = await orderRepository.findById(orderId);
    if (!order) {
      throw new OrderNotFoundError(orderId);
    }

    // Check if invoice already exists for this order and is a draft.
    const existingInvoice = await invoiceRepository.findDraft({
      by: "orderId",
      orderId,
    });
    if (!existingInvoice) {
      throw new InvoiceDraftNotFoundError(orderId);
    }
    // Do the check again, cause input might not be consistent
    if (!existingInvoice.requireStatus(InvoiceStatus.DRAFT)) {
      throw new InvoiceStateViolationError(
        invoiceNumber,
        InvoiceStatus.DRAFT,
        existingInvoice.getStatus()
      );
    }
    // Check if the invoice numbers match to prevent a MITM
    if (existingInvoice.invoiceNumber !== invoiceNumber) {
      throw new InvoiceIntegrityCheckError(
        invoiceNumber,
        "INVOICE_NUMBER_MISMATCH" as const,
        `Invoice draft has ${existingInvoice.invoiceNumber} but received ${invoiceNumber}`
      );
    }

    // Calculate shipping costs (has business rules)
    const baseTotal = order.subtotal.add(order.taxes).subtract(order.discount);
    const shippingCost = ShippingCost.determine(
      baseTotal,
      shippingRateProvider,
      shippingCostPolicy
    );
    // Now compute the grand total
    const grandTotal = baseTotal.add(shippingCost);

    // Prepare invoice financials
    const financials: InvoiceFinancials = {
      subtotal: order.subtotal,
      taxes: order.taxes,
      shippingCost: shippingCost,
      discount: order.discount,
      grandTotal,
    };
    // Prepare invoice data for PDF generation
    const invoiceDataForPdf: InvoiceData = {
      invoiceId: "", // Will be set after invoice creation
      invoiceNumber,
      orderId,
      customer: order.customer,
      items: order.items,
      financials,
      createdOn: new Date(),
      createdBy: issuedBy,
      legalInfo,
    };

    // Generate PDF
    const pdfBytes = await pdfRenderer.render(invoiceDataForPdf);

    // Issue a finalized invoice (which validates, emits event, etc.)
    // The IssueInvoiceData is the same as the existing invoice, which is an invoice draft.
    const invoice = await Invoice.issue(
      {
        orderId,
        invoiceNumber,
        customerSnapshot: existingInvoice.customerSnapshot,
        itemsSnapshot: existingInvoice.itemsSnapshot,
        financials,
        createdBy: issuedBy, // explicit issuer attribution
        status: InvoiceStatus.DRAFT,
      },
      pdfBytes,
      { invoiceNumberValidator }
    );

    // Persist invoice
    await invoiceRepository.save(invoice);

    // Publish the events
    if (deps.eventPublisher) {
      invoice.publishAllUncommitted(deps.eventPublisher);
    }

    // Return result
    const result: IssueInvoiceResult = {
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      orderId: invoice.orderId,
      grandTotal: invoice.financials.grandTotal.toPlainObject(),
      issuedOn: invoice.createdOn,
      issuedBy: invoice.createdBy,
    };

    return result;
  };
}

/**
 * Factory function to create IssueInvoice use case with dependencies
 */
export function createIssueInvoiceUseCase(
  deps: IssueInvoiceDependencies
): (command: IssueInvoiceCommand) => Promise<IssueInvoiceResult> {
  return makeIssueInvoice(deps);
}
